/**
 * SeedTestPlayersModal
 *
 * Debug-only modal for seeding fake disconnected players into an FPR game
 * for draft testing. Owns its own count/status state and the socket listener
 * for the seed-completed broadcast.
 */

import { useState, useEffect } from 'react';
import { useSocketContext } from '@swtorbit/shared-react-hooks';
import { SocketEvent } from '@swtorbit/shared-socket-events';

interface SeedTestPlayersModalProps {
  isOpen: boolean;
  onClose: () => void;
  roomCode: string;
}

export function SeedTestPlayersModal({ isOpen, onClose, roomCode }: SeedTestPlayersModalProps) {
  const { socket } = useSocketContext();
  const [seedCount, setSeedCount] = useState(8);
  const [seedLeaders, setSeedLeaders] = useState(2);
  const [seedTanks, setSeedTanks] = useState(2);
  const [seedHealers, setSeedHealers] = useState(2);
  const [seedDps, setSeedDps] = useState(4);
  const [seedMm, setSeedMm] = useState(3);
  const [seedStatus, setSeedStatus] = useState<string | null>(null);

  // Reset status whenever the modal opens
  useEffect(() => {
    if (isOpen) setSeedStatus(null);
  }, [isOpen]);

  // Listen for seed-completed broadcasts while modal is open
  useEffect(() => {
    if (!socket || !isOpen) return;

    const handleDummySeeded = (data: { success: boolean; seededCount?: number; error?: string }) => {
      setSeedStatus(data.success ? `✓ Seeded ${data.seededCount} players` : `Error: ${data.error}`);
    };

    socket.on(SocketEvent.DEV_DUMMY_PLAYERS_SEEDED, handleDummySeeded);
    return () => {
      socket.off(SocketEvent.DEV_DUMMY_PLAYERS_SEEDED, handleDummySeeded);
    };
  }, [socket, isOpen]);

  const handleSeed = () => {
    if (!socket) return;
    setSeedStatus(null);
    socket.emit(SocketEvent.DEV_SEED_DUMMY_PLAYERS, {
      roomCode,
      config: {
        count: seedCount,
        leaderCount: seedLeaders,
        tankCount: seedTanks,
        healerCount: seedHealers,
        dpsCount: seedDps,
        mmCount: seedMm,
      },
    });
  };

  if (!isOpen) return null;

  const fields: { label: string; value: number; min: number; max: number; set: (val: number) => void }[] = [
    { label: 'Total players', value: seedCount, min: 2, max: 30, set: setSeedCount },
    { label: 'Available as TL', value: seedLeaders, min: 0, max: seedCount, set: setSeedLeaders },
    { label: 'Tanks', value: seedTanks, min: 0, max: seedCount, set: setSeedTanks },
    { label: 'Healers', value: seedHealers, min: 0, max: seedCount, set: setSeedHealers },
    { label: 'DPS', value: seedDps, min: 0, max: seedCount, set: setSeedDps },
    { label: 'Know MM mechanics', value: seedMm, min: 0, max: seedCount, set: setSeedMm },
  ];

  return (
    <div className="" onClick={onClose}>
      <div className="modal text-white" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 400 }}>
        <h3>Seed Test Players</h3>
        <p className="text-white fs-sm mb-sm">
          Creates fake disconnected players with FPR registration data for draft testing.
        </p>
        <div className="d-flex flex-column gap-xs">
          {fields.map(({ label, value, min, max, set }) => (
            <div key={label} className="d-flex justify-content-between align-items-center gap-sm">
              <label className="fs-sm">{label}</label>
              <input
                type="number"
                value={value}
                min={min}
                max={max}
                onChange={(e) => set(Math.max(min, Math.min(max, Number(e.target.value))))}
                style={{ width: 60 }}
                className="form-input"
              />
            </div>
          ))}
        </div>
        {seedStatus && (
          <p className={`fs-sm mt-xs ${seedStatus.startsWith('✓') ? 'text-green-dark' : 'text-red'}`}>
            {seedStatus}
          </p>
        )}
        <div className="d-flex gap-xs mt-sm justify-content-end">
          <button className="btn btn--outline btn--slate" onClick={onClose}>Close</button>
          <button className="btn" onClick={handleSeed}>Seed Players</button>
        </div>
      </div>
    </div>
  );
}
